/**
 * ILT Learner Assistant chatbot constants.
 *
 * Centralizes the agent name, AG-UI streaming endpoint, branding strings, and
 * disclaimer copy for the learner-facing chatbot. Edit here to update copy or
 * routing across all ILT Learner Assistant surfaces.
 */

/**
 * Registered agent name in the AI Gateway / Intent Router routing config.
 * Forwarded with each request (AG-UI `forwardedProps.agent_name` + routing
 * header) so the gateway pins the request to the Knowledge Agent rather than
 * letting the intent router pick another domain agent. Confirmed against the
 * Knowledge Agent's CDK EventBridge rule (`agentName: ["knowledge-agent"]`).
 */
export const ILT_LEARNER_ASSISTANT_AGENT_NAME = 'knowledge-agent';

/**
 * AG-UI streaming endpoint for the ILT agent. Same-origin relative path so the
 * browser attaches the HttpOnly auth cookies automatically (ChatbotV2 sets
 * `credentials: 'include'`).
 *
 * This is the AI Gateway M2 streaming proxy route. In prod the AT2Gateway
 * CloudFront distribution routes `/api/aigateway/stream` to the ALB Fargate SSE
 * proxy (`realtime.{stage}.aigateway.skillbuilder.training.aws.dev` → `POST
 * /stream`), stripping the `/api/aigateway` prefix. Matches the route used by
 * the other ChatbotV2 consumers (SBAIR, UserOrg). The gateway pins the request
 * to this agent via the `ILT_LEARNER_ASSISTANT_AGENT_HEADER` below.
 *
 * NOTE: the streaming behavior is currently enabled for beta only, and
 * `knowledge-agent` must be registered in the gateway's domain agent registry
 * before requests will route end-to-end.
 */
export const ILT_LEARNER_ASSISTANT_ENDPOINT = '/api/aigateway/stream';

/**
 * Custom routing header the gateway expects to map a request to the agent
 * runtime. Mirrors the header used by the shared chatbot-ui consumers.
 */
export const ILT_LEARNER_ASSISTANT_AGENT_HEADER = 'x-amzn-bedrock-agentcore-runtime-custom-tnc-agent-name';

/**
 * sessionStorage key for cross-refresh conversation continuity — namespaced to
 * the ILT Learner Assistant so it doesn't collide with other chats on the same
 * origin.
 */
export const ILT_LEARNER_ASSISTANT_CONVERSATION_ID_STORAGE_KEY = 'ilt-learner-assistant-conversation-id';

/** Chat header title shown at the top of the panel. */
export const ILT_LEARNER_ASSISTANT_HEADER_TITLE = 'ILT Learner Assistant';

/** Welcome message shown as the first assistant bubble. */
export const ILT_LEARNER_ASSISTANT_WELCOME_MESSAGE = 'Hi! Ask me anything about your classes, schedule, or enrollment.';

/** AG-UI error code the AI Gateway sends when its guardrail blocks a message. */
export const GUARDRAIL_BLOCKED_ERROR_CODE = 'GUARDRAIL_BLOCKED';

/** Shown in place of the generic failed-turn copy when the guardrail blocks a message. */
export const ILT_LEARNER_ASSISTANT_GUARDRAIL_BLOCKED_MESSAGE =
    "I'm unable to respond to that request. Please try rephrasing your question or ask something else.";

/** Footer disclaimer lines — AI accuracy + privacy. */
export const ILT_LEARNER_ASSISTANT_FOOTER_LINES: readonly string[] = [
    'AI-generated content may be inaccurate.',
    'Do not include personal or sensitive information.',
];

/** Footer links — Responsible AI policy. */
export const ILT_LEARNER_ASSISTANT_FOOTER_LINKS: readonly { label: string; href: string }[] = [
    {
        label: 'AWS Responsible AI Policy',
        href: 'https://aws.amazon.com/machine-learning/responsible-ai/policy/',
    },
];
